import { MoldOrder, MoldOrderStatus } from '../../types/mold';
import { Section, Question, QuestionFilterStrategy } from '../../types/form';
// Phase 1 (load-time tag/status/rule filter) is owned by simpleFilterSections — the
// SAME implementation the live render path runs (getFormConfig → utils.ts). The
// resolver composes it rather than re-deriving it, so there is one Phase-1 evaluator.
// (utils→components import: a layering wart, but cycle-free — simpleFilterUtils imports
// only types.)
import { simpleFilterSections } from '../../components/Questionnaire/simpleFilterUtils';

/**
 * resolveVisibleQuestions — ONE pure function that reproduces production's
 * decision of which sections/questions are visible. Additive and
 * behavior-preserving: the differential test (resolveVisibleQuestions.diff.test.ts)
 * pins it to the live composition by executing the REAL production gates as the
 * oracle. The load-time tag/status/rule filter is DELEGATED to simpleFilterSections,
 * and the section/question gates share the isAllowedOrderType helper — so the resolver
 * runs the SAME functions as the render path, not parallel copies. (See ADR-0001 /
 * docs/.../verification for the origin/main equivalence argument.)
 *
 * Composition (matches Questionnaire/index.tsx):
 *   1. LOAD-TIME tag/status/rule filter — simpleFilterSections (simpleFilterUtils.ts).
 *   2. RENDER-TIME section gate — shouldShowSection against the EFFECTIVE type
 *      (orderDetail.moldOrderType).
 *   3. RENDER-TIME question gate — shouldShowQuestion against the RAW subtype
 *      (moldOrder.type) + dependsOn (equals / notEquals / equalsAny). The section
 *      gate's effective type and the question gate's raw subtype can differ; that
 *      asymmetry is preserved deliberately.
 *
 * SCOPE — question/section-level visibility ONLY. Deliberately EXCLUDED (the oracle
 * does not cover these, so excluding them is what keeps the differential test green)
 * and tracked as ADR Phase 1 follow-ups:
 *   - wizard step visibility (filterVisibleSteps): orthogonal to question
 *     visibility — visibleSteps feeds only the step bar and required-photo tags.
 *   - field-level showIf inside details questions (field, not question, visibility).
 *   - impure side effects: showIf value clearing, dependent-question value reset,
 *     conditional-attachment file cleanup.
 *   - showOnlyNG (pure UI), previewConfig overrides, quickAccessStrategy navigation.
 *
 * Purity: no input mutation, no side effects.
 */

export interface FormDefinition {
  sections: Section[];
  questionFilterStrategy?: QuestionFilterStrategy;
}

export interface ResolveContext {
  /** MoldOrder.status — drives statusTagFilters. */
  status: MoldOrderStatus;
  /** RAW subtype (MoldOrder.type, e.g. 'repair.MAJOR') — drives QUESTION-level allowedOrderTypes. */
  type: string;
  /**
   * Full MoldOrder. Provides dot-path properties for moldOrderRules + the
   * SECTION-level effective type (orderDetail.moldOrderType). Kept whole so the
   * dot-notation getter sees exactly what the production functions see.
   */
  moldOrder: MoldOrder;
}

/**
 * Project a MoldOrder into the resolver context. Single home for the
 * (status, type, moldOrder) shape so the resolveVisibleQuestions call sites
 * can't drift as ResolveContext evolves.
 */
export const buildResolveContext = (moldOrder: MoldOrder): ResolveContext => ({
  status: moldOrder.status,
  type: moldOrder.type,
  moldOrder,
});

export interface ResolvedQuestion {
  question: Question;
  sectionId: string;
  visible: boolean;
  reasons: string[];
}

type FormAnswers = Record<string, unknown>;

/**
 * allowedOrderTypes gate — the single home for the "absent allowedOrderTypes ⇒
 * visible, absent type ⇒ visible" rule (DEFAULT 3), shared by the section gate, the
 * question gate, and formProgress's shouldShowSection/shouldShowQuestion. A falsy
 * effective/raw type means "show": legacy orders may carry no
 * orderDetail.moldOrderType, and `[...].includes(undefined)` would otherwise wrongly
 * hide a typed section/question. Behavior-preserving for every real order (the type
 * is present in all of them).
 */
export const isAllowedOrderType = (
  allowedOrderTypes: readonly string[] | undefined,
  moldOrderType?: string,
): boolean =>
  !allowedOrderTypes ||
  !moldOrderType ||
  allowedOrderTypes.includes(moldOrderType);

/* ----------------------------------------------------------------------------
 * Load-bearing visibility defaults (§4 of 02-current-state-map.md). Only the
 * status fallback is a reusable predicate (below); the other three are
 * "absent ⇒ visible" rules encoded inline as `return true` at their decision
 * sites and documented here:
 *   - an absent/empty visibility condition ⇒ visible (absent questionFilterStrategy
 *     passes all sections unchanged; absent dependsOn ⇒ question shown).
 *   - absent allowedOrderTypes ⇒ visible (section and question).
 *   - a dependsOn with none of equals / notEquals / equalsAny ⇒ visible.
 * ------------------------------------------------------------------------- */

/**
 * Status fallback. When no status/default tag filter matches a section/question
 * (no includeAll, no includeTags), it is shown by default UNLESS
 * filterMode === 'inclusive' ("exclusive shows by default").
 */
export const STATUS_FALLBACK_SHOWS_WHEN_NOT_INCLUSIVE = (
  filterMode: QuestionFilterStrategy['filterMode'],
): boolean => filterMode !== 'inclusive';

/* ----------------------------------------------------------------------------
 * Phase 1 — load-time tag/status/rule filter.
 *
 * Delegated to simpleFilterSections (the production load-time filter); this re-keys
 * its output into the survivors/dropped shape the resolver loop consumes to build
 * reasons[]. Returns, for each section, the set of question ids that survive the tag
 * filter, plus the section ids dropped by the empty-section rule. With no strategy,
 * every section passes unchanged (no empty-section drop).
 * ------------------------------------------------------------------------- */

interface TagFilterResult {
  /** For sections that pass: ids of questions that survive the tag filter. */
  survivingQuestionIdsBySection: Map<string, Set<string>>;
  /** Section ids dropped by the empty-section rule (or never passing). */
  droppedSectionIds: Set<string>;
}

const passesTagFilter = (
  sections: Section[],
  status: MoldOrderStatus,
  moldOrder: MoldOrder,
  strategy: QuestionFilterStrategy | undefined,
): TagFilterResult => {
  const survivingQuestionIdsBySection = new Map<string, Set<string>>();
  const droppedSectionIds = new Set<string>();

  // No strategy — every section passes unchanged, with NO empty-section drop. Kept as
  // an explicit path (forward-looking infra; see useFormProgress) — simpleFilterSections
  // short-circuits identically, but the explicit branch documents the no-drop guarantee.
  if (!strategy) {
    for (const section of sections) {
      survivingQuestionIdsBySection.set(
        section.id,
        new Set(section.questions.map((q) => q.id)),
      );
    }
    return { survivingQuestionIdsBySection, droppedSectionIds };
  }

  // Delegate the tag/status/rule filter to the production implementation, then re-key
  // its output: a section it returns keeps the question ids it kept; a section it drops
  // (empty after filtering) becomes a dropped section.
  const filteredById = new Map(
    simpleFilterSections(sections, status, moldOrder, strategy).map((s) => [
      s.id,
      s,
    ]),
  );
  for (const section of sections) {
    const kept = filteredById.get(section.id);
    if (kept) {
      survivingQuestionIdsBySection.set(
        section.id,
        new Set(kept.questions.map((q) => q.id)),
      );
    } else {
      droppedSectionIds.add(section.id);
    }
  }

  return { survivingQuestionIdsBySection, droppedSectionIds };
};

/* ----------------------------------------------------------------------------
 * Phase 2 — render-time section gate (reimplements shouldShowSection).
 *   shouldShowSection uses moldOrder.orderDetail.moldOrderType, NOT the raw
 *   subtype. (formProgress.ts:601-604, index.tsx:214-215.)
 * ------------------------------------------------------------------------- */

/* ----------------------------------------------------------------------------
 * Phase 3 — render-time question gate (reimplements shouldShowQuestion EXACTLY).
 *   formProgress.ts:567-599. Uses moldOrder.type (raw subtype) for question
 *   allowedOrderTypes. Order: allowedOrderTypes → dependsOn(equals → notEquals →
 *   equalsAny) → default true.
 * ------------------------------------------------------------------------- */

const questionPassesRenderGate = (
  question: Question,
  answers: FormAnswers,
  rawType: string | undefined,
): { visible: boolean; reason: string } => {
  // question allowedOrderTypes — the shared gate (raw subtype). Fails only when
  // allowedOrderTypes and rawType are both present and the type is excluded
  // (formProgress.ts shouldShowQuestion).
  if (!isAllowedOrderType(question.allowedOrderTypes, rawType)) {
    return {
      visible: false,
      reason: 'question allowedOrderTypes excludes type',
    };
  }

  // DEFAULT 2 — absent dependsOn ⇒ visible.
  if (!question.dependsOn) {
    return { visible: true, reason: 'no dependsOn' };
  }

  const { questionId, equals, notEquals, equalsAny } = question.dependsOn as {
    questionId: string;
    equals?: unknown;
    notEquals?: unknown;
    equalsAny?: unknown[];
  };

  if (typeof equals !== 'undefined') {
    return answers[questionId] === equals
      ? { visible: true, reason: 'dependsOn.equals met' }
      : { visible: false, reason: 'dependsOn.equals not met' };
  }

  if (typeof notEquals !== 'undefined') {
    return answers[questionId] !== notEquals
      ? { visible: true, reason: 'dependsOn.notEquals met' }
      : { visible: false, reason: 'dependsOn.notEquals not met' };
  }

  if (Array.isArray(equalsAny)) {
    return equalsAny.includes(answers[questionId])
      ? { visible: true, reason: 'dependsOn.equalsAny met' }
      : { visible: false, reason: 'dependsOn.equalsAny not met' };
  }

  // DEFAULT 4 — unrecognized dependsOn key ⇒ visible.
  return {
    visible: true,
    reason: 'dependsOn unrecognized key (default visible)',
  };
};

/* ----------------------------------------------------------------------------
 * Public entry point.
 * ------------------------------------------------------------------------- */

/**
 * Resolves the visibility of every question in `def` against `ctx` + `answers`,
 * reproducing the current production decision. Returns one entry per question in
 * definition order with `{ question, sectionId, visible, reasons }`.
 *
 * A question is `visible` iff:
 *   - its section survived the load-time tag filter AND the question survived it
 *     (or there was no strategy), AND
 *   - the section passes shouldShowSection (section allowedOrderTypes vs
 *     orderDetail.moldOrderType), AND
 *   - the question passes shouldShowQuestion (question allowedOrderTypes vs
 *     ctx.type + dependsOn vs answers).
 */
export function resolveVisibleQuestions(
  def: FormDefinition,
  ctx: ResolveContext,
  answers: FormAnswers,
): ResolvedQuestion[] {
  const { status, type, moldOrder } = ctx;
  const sections = def.sections || [];

  // Phase 1 — load-time tag/status/rule filter.
  const tagFilter = passesTagFilter(
    sections,
    status,
    moldOrder,
    def.questionFilterStrategy,
  );

  // Section-level effective type for shouldShowSection (NOT the raw subtype).
  const effectiveType = moldOrder?.orderDetail?.moldOrderType;

  const result: ResolvedQuestion[] = [];

  for (const section of sections) {
    const sectionDroppedByTags = tagFilter.droppedSectionIds.has(section.id);
    const survivors = tagFilter.survivingQuestionIdsBySection.get(section.id);
    const sectionPassesType = isAllowedOrderType(
      section.allowedOrderTypes,
      effectiveType,
    );

    for (const question of section.questions) {
      const reasons: string[] = [];
      let visible = true;

      // Phase 1 result.
      if (sectionDroppedByTags) {
        visible = false;
        reasons.push('section dropped by empty-section rule (tag filter)');
      } else if (survivors && !survivors.has(question.id)) {
        visible = false;
        reasons.push('question removed by tag filter');
      } else {
        reasons.push('passed tag filter');
      }

      // Phase 2 — section render gate.
      if (visible) {
        if (!sectionPassesType) {
          visible = false;
          reasons.push('section allowedOrderTypes excludes effective type');
        } else {
          reasons.push('section allowedOrderTypes ok');
        }
      }

      // Phase 3 — question render gate.
      if (visible) {
        const gate = questionPassesRenderGate(question, answers, type);
        reasons.push(gate.reason);
        if (!gate.visible) {
          visible = false;
        }
      }

      result.push({ question, sectionId: section.id, visible, reasons });
    }
  }

  return result;
}

export default resolveVisibleQuestions;
